import {
    apiErrorToastMessage,
    wizardErrorMessage,
} from '../SftpIntegration/sftp-user-messages';

export { apiErrorToastMessage, wizardErrorMessage };

const ENCRYPTION_NOT_CONFIGURED = 'Credential encryption is not configured';

function isNetworkOrTimeout(err: unknown): boolean {
    const e = err as { code?: string; message?: string; response?: { status?: number } };
    if (e?.code === 'ECONNABORTED' || e?.code === 'ERR_NETWORK') {
        return true;
    }
    const m = (e?.message || '').toLowerCase();
    return m.includes('timeout') || m.includes('network error') || m.includes('failed to fetch');
}

/** User-facing message for integration wizard / hub API failures. */
export function integrationErrorMessage(err: unknown, fallback: string): string {
    if (isNetworkOrTimeout(err)) {
        return (
            `Connection lost or timed out. ${fallback} ` +
            'If you were saving partners, refresh AS2 Partners — your configuration may already be saved.'
        );
    }
    const e = err as { response?: { status?: number } };
    const status = e?.response?.status;
    if (status != null && status >= 500) {
        return `Server error (${status}). ${fallback} Wait a moment and retry.`;
    }
    if (status === 409) {
        return (
            'Partner group already exists. Use AS2 Partners to upload certificates, edit URLs, or test connection.'
        );
    }
    let msg = wizardErrorMessage(err, fallback);
    if (msg.includes('Empty input') || msg.includes('file is empty')) {
        return (
            'Certificate file is empty or invalid. Use Download reference certificate in this wizard ' +
            '(not /download/ URLs in the browser). Use the partner .cer/.pem file from your trading partner (test and production certs if they differ).'
        );
    }
    if (msg.includes('HTML error page')) {
        return msg;
    }
    // Avoid duplicated nested parse messages from the API
    while (msg.includes('Could not parse certificate: Could not parse')) {
        msg = msg.replace('Could not parse certificate: ', '');
    }
    if (msg.includes(ENCRYPTION_NOT_CONFIGURED)) {
        return (
            `${msg} Set INTEGRATION_CREDENTIALS_MASTER_KEY in backend/.env.local or .env.production ` +
            '(openssl rand -base64 32) and restart the server.'
        );
    }
    return msg;
}

export type ConnectionTestResult = {
    reachable?: boolean;
    httpOk?: boolean;
    statusCode?: number;
    latencyMs?: number;
    error?: string;
    warning?: string;
    tlsRelaxed?: boolean;
    as2Url?: string;
    environment?: string;
    partnerCode?: string;
    method?: string;
};

function envLabel(code: string, environment?: string): string {
    if (environment === 'TEST' || environment === 'PRODUCTION') {
        return environment;
    }
    if (code.endsWith('_TEST')) {
        return 'TEST';
    }
    return 'PRODUCTION';
}

/**
 * Formats admin AS2 reachability test results for toasts and inline banners.
 * From local Antar you can test both TEST and PRODUCTION partner URLs (public internet).
 */
export function formatConnectionTestResult(
    code: string,
    result: ConnectionTestResult
): { kind: 'success' | 'error'; message: string; info?: string } {
    const env = envLabel(code, result.environment);

    if (!result.reachable) {
        const err = (result.error || 'Partner URL could not be reached.').trim();
        const urlLine = result.as2Url ? `\nURL: ${result.as2Url}` : '';
        return {
            kind: 'error',
            message: `${code} (${env}): ${err}${urlLine}`,
        };
    }

    const status = result.statusCode ?? 0;
    const ms = result.latencyMs ?? 0;

    if (result.httpOk) {
        const message = `${code} (${env}): Partner is reachable — HTTP ${status} in ${(ms / 1000).toFixed(1)}s.`;
        let info: string | undefined;
        if (result.tlsRelaxed) {
            info = result.warning
                ? `Note: ${result.warning}`
                : 'Note: Partner TLS was verified with a relaxed check for this admin test.';
        }
        return { kind: 'success', message, info };
    }

    const statusText = status > 0 ? `HTTP ${status}` : 'Connected';
    const message = `${code} (${env}): Partner is reachable — ${statusText} in ${(ms / 1000).toFixed(1)}s.`;
    let info: string | undefined;
    if (result.tlsRelaxed) {
        info = result.warning
            ? `Note: ${result.warning}`
            : 'Note: Partner TLS was verified with a relaxed check for this admin test.';
    }
    return { kind: 'success', message, info };
}

/** One-line label for AS2 Partners table Connection column. */
export function formatConnectionTestShort(
    result: ConnectionTestResult
): { label: string; title?: string; ok: boolean } {
    if (!result.reachable) {
        return {
            ok: false,
            label: 'Unreachable',
            title: (result.error || 'Could not reach partner URL').trim(),
        };
    }
    const status = result.statusCode ?? 0;
    const ms = result.latencyMs ?? 0;
    if (result.httpOk) {
        return { ok: true, label: `OK ${status} (${ms}ms)`, title: result.warning };
    }
    if (status > 0) {
        return {
            ok: true,
            label: `Up ${status} (${ms}ms)`,
            title:
                (result.warning || '') +
                ' Host responded; non-200 is common for HEAD/GET on AS2 URLs.',
        };
    }
    return { ok: true, label: `Up (${ms}ms)`, title: result.warning };
}
